import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Palette, Sparkles, Download, Check, ArrowLeft, Loader2, 
  Type, RefreshCw, Save, Image as ImageIcon, Wand2, Upload, BookOpen, Info
} from 'lucide-react';
import { ImageUploadModal } from '../components/ImageUploadModal';

export function BookCoverDesigner() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [covers, setCovers] = useState([]);
  const [selectedCover, setSelectedCover] = useState(null);
  const [genres, setGenres] = useState([]);
  const [specifications, setSpecifications] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [genresLoaded, setGenresLoaded] = useState(false);
  
  // Generation form
  const [layoutType, setLayoutType] = useState('front_only');
  const [genre, setGenre] = useState('');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [mood, setMood] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  
  // Customization
  const [customizing, setCustomizing] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customAuthor, setCustomAuthor] = useState('');
  const [customSubtitle, setCustomSubtitle] = useState('');
  const [fontTitle, setFontTitle] = useState('');
  const [fontAuthor, setFontAuthor] = useState('');
  const [colorTitle, setColorTitle] = useState('');
  const [colorAuthor, setColorAuthor] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, documentId]);

  const loadData = async () => {
    try {
      // Load document
      const docResponse = await api.request(`/documents/${documentId}`);
      
      if (!docResponse) {
        throw new Error('Document not found');
      }
      
      setDocument(docResponse);
      setTitle(docResponse.title || '');
      setAuthor(user?.full_name || user?.email || '');
      
      // Load existing covers
      try {
        const coversResponse = await api.request(`/book-covers/document/${documentId}`);
        setCovers(coversResponse.covers || []);
      } catch (err) {
        console.log('No existing covers found');
        setCovers([]);
      }
      
      // Load genres
      const genresResponse = await api.request('/book-covers/genres');
      const loadedGenres = genresResponse.genres || [];
      setGenres(loadedGenres);
      
      // Set default genre if we have genres
      if (loadedGenres.length > 0 && !genre) {
        setGenre(loadedGenres[0].id);
      }
      setGenresLoaded(true);
      
      // Load specifications
      const specsResponse = await api.request('/book-covers/specifications');
      setSpecifications(specsResponse.specifications || null);
      
      // Load uploaded images
      try {
        const imagesResponse = await api.request('/book-covers/uploaded-images');
        setUploadedImages(imagesResponse.images || []);
      } catch (err) {
        console.log('No uploaded images found');
        setUploadedImages([]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        alert('Authentication error. Please log in again.');
        navigate('/login');
      } else if (error.message.includes('404') || error.message.includes('not found')) {
        alert('Document not found. Returning to dashboard.');
        navigate('/dashboard');
      } else {
        alert(`Failed to load document: ${error.message}`);
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = async (image) => {
    setUploadedImages([image, ...uploadedImages]);
    setShowUploadModal(false);
    alert('✅ Image uploaded! You can now use it to create a cover.');
  };

  const handleGenerate = async () => {
    if (!title || !author) {
      alert('Please provide at least a title and author name');
      return;
    }

    if (!genre) {
      alert('Please select a genre');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.request('/book-covers/generate', {
        method: 'POST',
        body: JSON.stringify({
          document_id: documentId,
          genre,
          title,
          author,
          subtitle,
          description,
          mood,
          custom_prompt: customPrompt
        })
      });

      if (response.success) {
        alert(`✅ Generated ${response.covers.length} cover variations! Scroll down to see them.`);
        setCovers([...response.covers, ...covers]);
      }
    } catch (error) {
      console.error('Error generating covers:', error);
      alert('Failed to generate covers. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectCover = async (cover) => {
    setSelectedCover(cover);
    setCustomTitle(cover.title);
    setCustomAuthor(cover.author);
    setCustomSubtitle(cover.subtitle || '');
    setFontTitle(cover.customization?.font_title || '');
    setFontAuthor(cover.customization?.font_author || '');
    setColorTitle(cover.customization?.color_title || '');
    setColorAuthor(cover.customization?.color_author || '');
    setCustomizing(true);
  };

  const handleSaveCustomization = async () => {
    if (!selectedCover) return;

    try {
      await api.request('/book-covers/customize', {
        method: 'POST',
        body: JSON.stringify({
          cover_id: selectedCover.id,
          title: customTitle,
          author: customAuthor,
          subtitle: customSubtitle,
          font_title: fontTitle,
          font_author: fontAuthor,
          color_title: colorTitle,
          color_author: colorAuthor
        })
      });

      alert('✅ Cover customization saved!');
      loadData();
    } catch (error) {
      alert('Failed to save customization');
    }
  };

  const handleFinalize = async (coverId) => {
    if (!confirm('Mark this cover as final? This will attach it to your document for Kindle publishing.')) {
      return;
    }

    try {
      const response = await api.request(`/book-covers/finalize/${coverId}`, {
        method: 'POST'
      });

      if (response.success) {
        alert('✅ Cover finalized! Your book is now Kindle-ready.');
        navigate(`/editor/${documentId}`);
      }
    } catch (error) {
      alert('Failed to finalize cover');
    }
  };

  const handleDelete = async (coverId) => {
    if (!confirm('Delete this cover design?')) return;

    try {
      await api.request(`/book-covers/cover/${coverId}`, {
        method: 'DELETE'
      });
      
      setCovers(covers.filter(c => c.id !== coverId));
      alert('Cover deleted');
    } catch (error) {
      alert('Failed to delete cover');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Upload Modal */}
      <ImageUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/editor/${documentId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Book Cover Designer</h1>
                <p className="text-sm text-gray-600">{document?.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Kindle KDP Ready
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Generation Form */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-purple-600" />
                  Generate AI Cover
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Button */}
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 font-medium"
                >
                  <Upload className="w-5 h-5" />
                  Upload Your Own Image
                </button>

                {uploadedImages.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <p className="text-green-800">
                      ✓ {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''} uploaded
                    </p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Or generate with AI:</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Layout Type *</label>
                  <select
                    value={layoutType}
                    onChange={(e) => setLayoutType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="front_only">📕 Front Cover Only (eBook)</option>
                    <option value="back_only">📖 Back Cover Only</option>
                    <option value="full_wrap">📚 Full Wrap (Front + Spine + Back)</option>
                  </select>
                  {specifications && (
                    <p className="text-xs text-gray-500 mt-1">
                      {layoutType === 'front_only' && `Dimensions: ${specifications.ebook.width}x${specifications.ebook.height}px`}
                      {layoutType === 'back_only' && `Dimensions: ${specifications.print_6x9.width}x${specifications.print_6x9.height}px`}
                      {layoutType === 'full_wrap' && `Dimensions: ${specifications.print_full_wrap.width}x${specifications.print_full_wrap.height}px (includes bleed)`}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Genre *</label>
                  <select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    disabled={!genresLoaded || genres.length === 0}
                  >
                    {!genresLoaded && <option value="">Loading genres...</option>}
                    {genresLoaded && genres.length === 0 && <option value="">No genres available</option>}
                    {genres.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  {genres.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {genres.find(g => g.id === genre)?.keywords?.join(', ')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Book Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Enter your book title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Author Name *</label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Subtitle (Optional)</label>
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Book subtitle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Brief description for AI (e.g., 'A thrilling space adventure with aliens')"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Mood/Atmosphere</label>
                  <input
                    type="text"
                    value={mood}
                    onChange={(e) => setMood(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="e.g., dark, uplifting, mysterious"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Custom Instructions</label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    rows="2"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Additional details for the AI"
                  />
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={generating || !title || !author}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate 3 Covers
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  AI will generate 3 high-quality cover variations
                  <br />
                  Kindle KDP compliant (2560x1600px)
                </p>
              </CardContent>
            </Card>

            {/* Customization Panel */}
            {customizing && selectedCover && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-600" />
                    Customize Text
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <input
                      type="text"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Author</label>
                    <input
                      type="text"
                      value={customAuthor}
                      onChange={(e) => setCustomAuthor(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={customSubtitle}
                      onChange={(e) => setCustomSubtitle(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Title Color</label>
                      <input
                        type="color"
                        value={colorTitle}
                        onChange={(e) => setColorTitle(e.target.value)}
                        className="w-full h-10 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Author Color</label>
                      <input
                        type="color"
                        value={colorAuthor}
                        onChange={(e) => setColorAuthor(e.target.value)}
                        className="w-full h-10 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSaveCustomization}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Save className="w-4 h-4" />
                    Save Customization
                  </button>

                  <button
                    onClick={() => setCustomizing(false)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Generated Covers */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-purple-600" />
                    Your Cover Designs
                  </div>
                  <span className="text-sm text-gray-500">
                    {covers.length} cover{covers.length !== 1 ? 's' : ''}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {covers.length === 0 ? (
                  <div className="text-center py-16">
                    <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Covers Yet</h3>
                    <p className="text-gray-600 mb-4">
                      Generate your first AI-powered book cover using the form on the left
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {covers.map((cover) => (
                      <div key={cover.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition">
                        <div className="relative aspect-[8/5] bg-gray-100">
                          <img
                            src={cover.image_url}
                            alt={cover.title}
                            className="w-full h-full object-cover"
                          />
                          {cover.status === 'final' && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Final
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h4 className="font-semibold mb-1">{cover.title}</h4>
                          <p className="text-sm text-gray-600 mb-1">by {cover.author}</p>
                          <p className="text-xs text-gray-500 mb-3">
                            Genre: {cover.genre} • {new Date(cover.created_at).toLocaleDateString()}
                          </p>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSelectCover(cover)}
                              className="flex-1 px-3 py-2 border border-purple-600 text-purple-600 rounded hover:bg-purple-50 text-sm flex items-center justify-center gap-1"
                            >
                              <Type className="w-4 h-4" />
                              Customize
                            </button>
                            {cover.status !== 'final' && (
                              <button
                                onClick={() => handleFinalize(cover.id)}
                                className="flex-1 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center justify-center gap-1"
                              >
                                <Check className="w-4 h-4" />
                                Finalize
                              </button>
                            )}
                          </div>

                          {cover.status !== 'final' && (
                            <button
                              onClick={() => handleDelete(cover.id)}
                              className="w-full mt-2 px-3 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
